import { useEffect, useState, useMemo } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  getPermisosDeRol,
  getPermisosDeUsuario,
  listPermisos,
  updatePermisosDeRol,
  updatePermisosDeUsuario,
  type Permiso,
} from "@/api/usuarios";
import { Button } from "@/components/ui/Button";
import { showToast } from "@/components/ui/Toast";
import { SkeletonText } from "@/components/ui/Skeleton";
import { PageHeader } from "@/components/ui/PageHeader";

export type PermisosMode = "rol" | "usuario";

export interface PermisosEditorProps {
  mode: PermisosMode;
  targetId: number;
  title: string;
  subtitle?: string;
  onSaved: () => void;
  onCancel?: () => void;
  submitLabel?: string;
}

const MODULE_GROUPS = [
  {
    title: "Gestionar NITs",
    prefixes: ["ver_nits", "editar_nits", "borrar_nits"],
  },
  {
    title: "Facturas de Compra",
    prefixes: [
      "ver_facturas_compra",
      "editar_facturas_compra",
      "borrar_facturas_compra",
    ],
  },
  {
    title: "Facturas de Ventas",
    prefixes: [
      "ver_facturas_ventas",
      "editar_facturas_ventas",
      "borrar_facturas_ventas",
    ],
  },
  {
    title: "Facturas Exterior",
    prefixes: [
      "ver_facturas_exterior",
      "editar_facturas_exterior",
      "borrar_facturas_exterior",
    ],
  },
  {
    title: "Legalización de Gastos",
    prefixes: [
      "ver_legalizacion_gastos",
      "editar_legalizacion_gastos",
      "borrar_legalizacion_gastos",
    ],
  },
  {
    title: "Gestión de Usuarios",
    prefixes: ["ver_usuarios", "editar_usuarios", "borrar_usuarios"],
  },
  {
    title: "Gestión de Empresas",
    prefixes: ["ver_empresas", "editar_empresas", "borrar_empresas"],
  },
];

const getActionLabel = (nombre: string): string => {
  if (nombre.startsWith("ver_")) return "Ver";
  if (nombre.startsWith("editar_")) return "Editar";
  if (nombre.startsWith("borrar_")) return "Borrar";
  return nombre;
};

export const PermisosEditor = ({
  mode,
  targetId,
  title,
  subtitle,
  onSaved,
  onCancel,
  submitLabel = "Guardar permisos",
}: PermisosEditorProps) => {
  const queryClient = useQueryClient();
  const [selected, setSelected] = useState<Set<number>>(new Set());

  const { data: allPerms = [], isLoading: loadingAll } = useQuery({
    queryKey: ["permisos"],
    queryFn: listPermisos,
  });

  const assignedQuery = useQuery({
    queryKey:
      mode === "rol"
        ? ["rol-permisos", targetId]
        : ["usuario-permisos", targetId],
    queryFn: () =>
      mode === "rol"
        ? getPermisosDeRol(targetId)
        : getPermisosDeUsuario(targetId),
    enabled: Number.isFinite(targetId),
  });

  useEffect(() => {
    if (assignedQuery.data) {
      setSelected(new Set(assignedQuery.data));
    }
  }, [assignedQuery.data]);

  const save = useMutation({
    mutationFn: (permisos_ids: number[]) =>
      mode === "rol"
        ? updatePermisosDeRol(targetId, permisos_ids)
        : updatePermisosDeUsuario(targetId, permisos_ids),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["permisos"] });
      showToast("success", "Permisos actualizados");
      onSaved();
    },
    onError: (err: Error) =>
      showToast("error", "No se pudo guardar", err.message),
  });

  const toggle = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const onSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    save.mutate(Array.from(selected));
  };

  const groupedPerms = useMemo(() => {
    const groups: { title: string; items: Permiso[] }[] = [];
    const processedIds = new Set<number>();

    MODULE_GROUPS.forEach((group) => {
      const items = allPerms.filter((p) => {
        const matches = group.prefixes.includes(p.nombre);
        if (matches) processedIds.add(p.id);
        return matches;
      });
      items.sort((a, b) => {
        const order = ["ver_", "editar_", "borrar_"];
        const aAct = order.findIndex((o) => a.nombre.startsWith(o));
        const bAct = order.findIndex((o) => b.nombre.startsWith(o));
        return aAct - bAct;
      });
      if (items.length > 0) {
        groups.push({ title: group.title, items });
      }
    });

    const remaining = allPerms.filter((p) => !processedIds.has(p.id));
    if (remaining.length > 0) {
      groups.push({ title: "Otros Permisos", items: remaining });
    }

    return groups;
  }, [allPerms]);

  return (
    <form onSubmit={onSubmit} className="space-y-6">
      <PageHeader title={title} subtitle={subtitle} />

      <div className="space-y-4">
        {loadingAll || assignedQuery.isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-card">
            <SkeletonText lines={5} />
          </div>
        ) : allPerms.length === 0 ? (
          <div className="rounded-lg border border-slate-200 bg-white p-6 shadow-card">
            <p className="text-sm text-ink-secondary">
              No hay permisos definidos.
            </p>
          </div>
        ) : (
          groupedPerms.map((group) => (
            <div
              key={group.title}
              className="rounded-lg border border-slate-200 bg-white p-5 shadow-card space-y-4"
            >
              <h3 className="text-sm font-semibold text-ink-primary border-b border-slate-100 pb-2">
                {group.title}
              </h3>
              <div className="grid gap-3 sm:grid-cols-3">
                {group.items.map((p) => {
                  const isChecked = selected.has(p.id);
                  return (
                    <label
                      key={p.id}
                      className={`flex items-start gap-3 rounded-md border p-3 cursor-pointer transition-all ${
                        isChecked
                          ? "border-accent-blue/30 bg-accent-blue/5 hover:bg-accent-blue/10"
                          : "border-slate-100 bg-slate-50 hover:bg-slate-100"
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggle(p.id)}
                        className="mt-1 h-4 w-4 rounded border-slate-300 text-accent-blue focus:ring-accent-blue focus:ring-offset-0"
                      />
                      <div>
                        <p className="text-sm font-medium text-ink-primary">
                          {getActionLabel(p.nombre)}
                        </p>
                        <p className="text-xs text-ink-secondary mt-0.5 leading-relaxed">
                          {p.descripcion}
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </div>
          ))
        )}
      </div>

      <div className="flex justify-end gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => (onCancel ? onCancel() : history.back())}
        >
          Cancelar
        </Button>
        <Button type="submit" loading={save.isPending}>
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};